import { MdAdd, MdModeEditOutline, MdSearch } from "react-icons/md";
import { FiInfo, FiTrash2 } from "react-icons/fi";
import { useVendor } from "../VendorMaster/Hooks/useVendor";
import { useProductMaster } from "../Inventory/Hooks/useProductMaster";
import { useState, useEffect } from "react";
import {
  GenericTable,
  Pagination,
  Spinner,
  SearchableDropdown,
} from "../../Components/Common";
import { VENDOR_COLUMNS, VENDOR_LABELS } from "./constants";
import { CREATE_VENDOR, DETAIL_VENDOR, EDIT_VENDOR } from "../../routes";
import { useNavigate } from "react-router-dom";
import { TOAST_MESSAGES } from "./constants";

const VendorMasterList = () => {
  const navigate = useNavigate();
  const {
    loading,
    page,
    totalPages,
    rowsPerPage,
    filters,
    setFilters,
    onPageChange,
    onPageSizeChange,
    fetchAllVendors,
    removeVendor,
  } = useVendor();

  const { fetchProducts } = useProductMaster();

  const [vendors, setVendors] = useState([]);

  const fetchVendor = async () => {
    const data = await fetchAllVendors();
    setVendors(data || []);
  };

  const handleFilterChange = (key, value) => {
    const updated = { ...filters, [key]: value };
    setFilters(updated);
    onPageChange(1);
  };

  useEffect(() => {
    fetchVendor();
  }, [page, rowsPerPage, filters]);

  const handleAddVendor = () => navigate(CREATE_VENDOR);
  const handleDetailVendor = (id) =>
    navigate(DETAIL_VENDOR.replace(":instanceId", id));
  const handleEditVendor = (id) =>
    navigate(EDIT_VENDOR.replace(":instanceId", id));

  const handleDeleteVendor = async (id) => {
    const confirmed = window.confirm(TOAST_MESSAGES.VENDOR_DELETE_ALERT);
    if (!confirmed) return;

    const success = await removeVendor(id);
    if (success) fetchVendor();
  };

  // Mobile card rendering
  const renderMobileFields = (vendors) => (
    <div className="mb-2 relative p-2 border rounded-md bg-white mt-2">
      <div
        onClick={() => handleDetailVendor(vendors.id)}
        className="p-4 bg-white rounded-lg shadow cursor-pointer hover:shadow-md transition-shadow"
      >
        <div className="flex justify-between items-start">
          <div className="text-md font-bold text-gray-900 truncate">
            {vendors.name}
          </div>
          <div
            className="flex items-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-row justify-end items-center gap-4">
              <span>
                {/* Edit Button */}
                <MdModeEditOutline
                  size={18}
                  className="h-5 w-5 text-blue-500 cursor-pointer hover:text-blue-700 transition-colors"
                  onClick={() => handleEditVendor(vendors.id)}
                />
              </span>
              <span>
                {/* Delete Button */}
                <FiTrash2
                  size={18}
                  className="cursor-pointer text-red-500 hover:text-red-700"
                  onClick={() => handleDeleteVendor(vendors.id)}
                />
              </span>
            </div>
          </div>
        </div>
        <div className="flex justify-between items-start">
          <div className="text-sm text-gray-700 mt-1">{vendors.mobile}</div>
          <div
            className="text-sm text-gray-700 mt-1 max-w-[150px] truncate"
            title={vendors.address}
          >
            {vendors.address || "-"}
          </div>
        </div>
      </div>
    </div>
  );

  // Table field rendering
  const renderFields = (key, row) => {
    switch (key) {
      case "name":
        return row.name || "-";
      case "contact_person_name":
        return row.contact_person_name;
      case "mobile":
        return row.mobile;
      case "address":
        return row.address || "-";
      case "gst_details":
        return row.gst_details || "-";
      case "actions":
        return (
          <div className="flex items-center gap-4">
            {/* Detail Button */}
            <FiInfo
              size={17}
              onClick={() => handleDetailVendor(row.id)}
              className="h-5 w-5 text-blue-500 cursor-pointer hover:text-blue-700 transition-colors"
            />
            {/* Edit Button */}
            <MdModeEditOutline
              size={17}
              onClick={() => handleEditVendor(row.id)}
              className="text-blue-500 cursor-pointer hover:text-blue-700 transition-colors"
            />
            {/* Delete button */}
            <FiTrash2
              size={17}
              className="cursor-pointer text-red-500 hover:text-red-700"
              onClick={() => handleDeleteVendor(row.id)}
            />
          </div>
        );
      default:
        return row[key] || "-";
    }
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          Vendor Master
        </h1>
        <button
          onClick={handleAddVendor}
          className="bg-primary text-white px-2 py-1 rounded-md flex items-center gap-2"
        >
          <MdAdd size={18} />
          <span className="whitespace-nowrap">Add Vendor</span>
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-md border px-4 py-3">
        <div className="flex gap-4 mb-4">
            <div className="relative md:w-1/4 w-3/4">
              <MdSearch
                size={20}
                className="absolute md:left-2 mt-2 left-2 text-gray-500"
              />
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                placeholder="Search by vendor name"
                className="w-full rounded-lg border border-gray-300 bg-white text-sm pl-9 pr-8 placeholder-gray-400 focus:ring-1 focus:border-blue-500 outline-none transition duration-150"
              />
              {filters.search && (
                <button
                  type="button"
                  onClick={() => handleFilterChange("search", "")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
            <SearchableDropdown
              fetchFunction={fetchProducts}
              onSelect={(val) => setFilters({ ...filters, product: val })}
              valueKey="id"
              labelKey="product_name"
              includeAllOption
              allOptionLabel="All Products"
              placeholder={"Select Products"}
              width="64"
            />
        </div>

        <div>
          {/* Desktop View */}
          <div className="md:block h-[50vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
            {loading ? (
              <div className="flex justify-center items-center h-[30vh]">
                <Spinner />
              </div>
            ) : (
              <GenericTable
                tableColumns={VENDOR_COLUMNS}
                tableRows={vendors}
                columnLabels={VENDOR_LABELS}
                renderField={renderFields}
                enableGridLayout={true}
                mobileGridColumns={1}
                renderMobileCard={renderMobileFields}
              />
            )}
          </div>

          {/* Pagination */}
          <div className="pt-3">
            <Pagination
              page={page}
              rowsPerPage={rowsPerPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorMasterList;
